/**
 * Proximity Preload Strategy
 *
 * Preloads when mouse gets close to a target element.
 * Perfect for button triggers - preload when user hovers near the button.
 */

import type { PreloadStrategy } from './types';
import { createLogger } from '../../shared/logger';

const logger = createLogger('ProximityPreload');

export class ProximityPreloadStrategy implements PreloadStrategy {
	private selector: string;
	private proximityPx: number;
	private hasTriggered = false;
	private boundHandler: (event: Event) => void;
	private moveEventType: 'pointermove' | 'mousemove' | null = null;
	private onPreloadCallback: (() => void) | null = null;
	private cachedElements: Element[] | null = null;
	private cacheRefreshInterval: ReturnType<typeof setInterval> | null = null;

	/**
	 * @param selector    CSS selector for the target element(s)
	 * @param proximityPx Distance in pixels to trigger preload (default 100px)
	 */
	constructor(selector: string, proximityPx = 100) {
		logger.log(
			'constructor - selector:',
			selector,
			'proximityPx:',
			proximityPx
		);
		this.selector = selector;
		this.proximityPx = proximityPx;
		this.boundHandler = this.handleMove.bind(this);
	}

	/**
	 * Start listening for mouse proximity
	 */
	public init(onPreload: () => void): void {
		logger.log('init() called');
		this.onPreloadCallback = onPreload;

		// Cache elements initially and refresh periodically for dynamic content
		this.refreshElementCache();
		this.cacheRefreshInterval = setInterval(
			() => this.refreshElementCache(),
			2000
		);

		this.attachMoveListener();
	}

	/**
	 * Refresh the cached elements list
	 */
	private refreshElementCache(): void {
		this.cachedElements = Array.from(
			document.querySelectorAll(this.selector)
		);
		logger.log(
			'Element cache refreshed, found:',
			this.cachedElements.length
		);
	}

	/**
	 * Stop listening
	 */
	public destroy(): void {
		logger.log('destroy() called');
		this.detachMoveListener();
		this.onPreloadCallback = null;
		this.cachedElements = null;
		if (this.cacheRefreshInterval) {
			clearInterval(this.cacheRefreshInterval);
			this.cacheRefreshInterval = null;
		}
	}

	/**
	 * Reset the strategy to allow re-preloading
	 * Called when popup is dismissed and we want to preload again on next hover
	 */
	public reset(): void {
		if (!this.hasTriggered) {
			return; // Already reset, nothing to do
		}

		logger.log('reset() called - re-arming proximity detection');
		this.hasTriggered = false;

		// Refresh cache and restart interval
		this.refreshElementCache();
		if (!this.cacheRefreshInterval && this.onPreloadCallback) {
			this.cacheRefreshInterval = setInterval(
				() => this.refreshElementCache(),
				2000
			);
		}

		// Re-attach listener if we have a callback
		if (this.onPreloadCallback) {
			this.attachMoveListener();
		}
	}

	/**
	 * Listen for real mouse/trackpad movement without reacting to the synthetic
	 * mouse events that iOS emits for taps. Changing the DOM during iOS's
	 * synthetic mousemove cancels the click that follows it.
	 */
	private attachMoveListener(): void {
		if (typeof window.PointerEvent !== 'undefined') {
			this.moveEventType = 'pointermove';
		} else if (
			typeof window.matchMedia !== 'function' ||
			window.matchMedia('(hover: hover) and (pointer: fine)').matches
		) {
			// Older browsers without Pointer Events may still preload when they
			// report a hover-capable, fine pointer.
			this.moveEventType = 'mousemove';
		} else {
			this.moveEventType = null;
			logger.log('No hover-capable pointer, skipping proximity preload');
			return;
		}

		document.addEventListener(this.moveEventType, this.boundHandler, {
			passive: true,
		});
	}

	private detachMoveListener(): void {
		if (!this.moveEventType) {
			return;
		}

		document.removeEventListener(this.moveEventType, this.boundHandler);
	}

	/**
	 * Check if mouse is near any target element
	 */
	private handleMove(event: Event): void {
		// Pointer Events identify the input device directly. Ignore touch and pen
		// movement so a tap cannot trigger DOM-changing proximity preloading.
		if (
			this.moveEventType === 'pointermove' &&
			(event as PointerEvent).pointerType !== 'mouse'
		) {
			return;
		}

		if (this.hasTriggered || !this.onPreloadCallback) {
			return;
		}

		const elements = this.cachedElements;
		if (!elements || elements.length === 0) {
			return;
		}

		const mouseEvent = event as MouseEvent;
		const mouseX = mouseEvent.clientX;
		const mouseY = mouseEvent.clientY;

		for (const element of elements) {
			const rect = element.getBoundingClientRect();

			// Calculate distance from mouse to element edges
			const distanceX = Math.max(
				rect.left - mouseX,
				0,
				mouseX - rect.right
			);
			const distanceY = Math.max(
				rect.top - mouseY,
				0,
				mouseY - rect.bottom
			);
			const distance = Math.sqrt(
				distanceX * distanceX + distanceY * distanceY
			);

			if (distance <= this.proximityPx) {
				logger.log(
					'Mouse within proximity of element! Distance:',
					distance,
					'px'
				);
				this.hasTriggered = true;
				this.detachMoveListener();

				// Stop cache refresh since we've triggered
				if (this.cacheRefreshInterval) {
					clearInterval(this.cacheRefreshInterval);
					this.cacheRefreshInterval = null;
				}

				this.onPreloadCallback();
				return;
			}
		}
	}
}
